/**
 * ADA Fix - Correction of Color Contrast
 * 
 * This script identifies and automatically fixes color contrast problems
 * that do not meet WCAG 2.2 (minimum ratio 4.5:1 for normal text).
 * 
 * Corrected problems:
 * 1. White text (#FFFFFF) on sky blue background (#7BA6DE) - ratio 2.5:1
 * 2. Red text (#C42323) on light blue background (#C7D9F1) - ratio 4.1:1
 * 
 * Meets:
 * - WCAG 2.2: 1.4.3 Contrast (Minimum) - Level AA
 * - WCAG 2.2: 1.4.6 Contrast (Enhanced) - Level AAA (when possible)
 * 
 * Author: ADA Compliance Assistant
 * Date: 2025
 */

(function() {
    'use strict';
    
        console.log('🎨 ADA Fix: Starting color contrast correction...');
    
    // Configuration of contrast corrections
    const CONTRAST_FIXES = {
        // Problematic colors and their corrections
        colorMappings: {
            // Problem 1: White text on sky blue background
            '#7BA6DE': {
                originalBg: '#7BA6DE',
                originalText: '#FFFFFF',
                originalRatio: 2.5,
                // Correction options
                fixes: [
                    {
                        type: 'darken_background',
                        newBg: '#2E6DA4', // Darker blue
                        newText: '#FFFFFF',
                        ratio: 4.6,
                        description: 'Background color changed to a darker blue'
                    },
                    {
                        type: 'darken_text',
                        newBg: '#7BA6DE',
                        newText: '#1A365D', // Very dark blue
                        ratio: 4.8,
                        description: 'Text color changed to a darker blue'
                    }
                ]
            },
            
            // Problem 2: Red text on blue background
            '#C7D9F1': {
                originalBg: '#C7D9F1',
                originalText: '#C42323',
                originalRatio: 4.1,
                fixes: [
                    {
                        type: 'darken_text',
                        newBg: '#C7D9F1',
                        newText: '#8B0000', // Darker red
                        ratio: 4.7,
                        description: 'Text color changed to a darker red'
                    },
                    {
                        type: 'lighten_background',
                        newBg: '#E8F2FF', // Lighter blue
                        newText: '#C42323',
                        ratio: 4.6,
                        description: 'Background color changed to a lighter blue'
                    },
                    {
                        type: 'high_contrast',
                        newBg: '#C7D9F1',
                        newText: '#000000', // Black for maximum contrast
                        ratio: 8.2,
                        description: 'Text color changed to black'
                    }
                ]
            }
        },
        
        // CSS selectors to apply corrections
        selectors: {
            // Elements with sky blue background (#7BA6DE) - Issue 18
            skyBlueBackground: [
                '[style*="#7BA6DE"]',
                '[style*="#7ba6de"]',
                '[style*="rgb(123, 166, 222)"]',
                '[style*="background-color:#7BA6DE"]',
                '[style*="background:#7BA6DE"]',
                '.sky-blue-bg',
                '.collection-card:hover',
                '.button--sky-blue',
                '.promo-banner[style*="7BA6DE"]',
                '.announcement-bar[style*="7BA6DE"]',
                '.discount-banner[style*="7BA6DE"]'
            ],
            
            // Elements with sky blue TEXT (#7BA6DE) - Issue 18
            skyBlueText: [
                '[style*="color:#7BA6DE"]',
                '[style*="color: #7BA6DE"]',
                '[style*="color:#7ba6de"]',
                '[style*="color: #7ba6de"]',
                '[style*="color:rgb(123, 166, 222)"]',
                '[style*="color: rgb(123, 166, 222)"]',
                '.text-sky-blue',
                '.stock-status[style*="7BA6DE"]',
                '.product-availability[style*="7BA6DE"]',
                '.in-stock[style*="7BA6DE"]',
                '.review-count[style*="7BA6DE"]',
                '.rating-text[style*="7BA6DE"]',
                '.okendo-review[style*="7BA6DE"]',
                '.okendo-review-count[style*="7BA6DE"]',
                '.okendo-review-text[style*="7BA6DE"]',
                'a[style*="color: #7BA6DE"]',
                'span[style*="color: #7BA6DE"]',
                'p[style*="color: #7BA6DE"]',
                'div[style*="color: #7BA6DE"]'
            ],
            
            // Elements with light blue background
            lightBlueBackground: [
                '[style*="#C7D9F1"]',
                '[style*="#c7d9f1"]',
                '.shopify-payment-button__button--unbranded',
                'button.shopify-payment-button__button--unbranded',
                '.light-blue-bg',
                '.klaviyo-form-error'
            ],
            
            // Elements with red text
            redText: [
                '[style*="color: #C42323"]',
                '[style*="color: #c42323"]',
                '.error_message',
                '.form-message.error',
                '.text-red',
                '.klaviyo .error_message'
            ]
        }
    };
    
    // Function to calculate relative luminance
    function getRelativeLuminance(hex) {
        // Convert hex to RGB
        const r = parseInt(hex.slice(1, 3), 16) / 255;
        const g = parseInt(hex.slice(3, 5), 16) / 255;
        const b = parseInt(hex.slice(5, 7), 16) / 255;
        
        // Apply gamma correction
        const toLinear = (c) => c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
        
        const rLinear = toLinear(r);
        const gLinear = toLinear(g);
        const bLinear = toLinear(b);
        
        // Calculate luminance
        return 0.2126 * rLinear + 0.7152 * gLinear + 0.0722 * bLinear;
    }
    
    // Function to calculate contrast ratio
    function getContrastRatio(color1, color2) {
        const lum1 = getRelativeLuminance(color1);
        const lum2 = getRelativeLuminance(color2);
        
        const lighter = Math.max(lum1, lum2);
        const darker = Math.min(lum1, lum2);
        
        return (lighter + 0.05) / (darker + 0.05);
    }
    
    // Function to extract colors from an element
    function getElementColors(element) {
        const computedStyle = window.getComputedStyle(element);
        const backgroundColor = computedStyle.backgroundColor;
        const color = computedStyle.color;
        
        // Convert RGB to HEX if necessary
        const rgbToHex = (rgb) => {
            if (rgb.startsWith('#')) return rgb;
            const match = rgb.match(/rgb\((\d+),\s*(\d+),\s*(\d+)\)/);
            if (!match) return null;
            
            const r = parseInt(match[1]).toString(16).padStart(2, '0');
            const g = parseInt(match[2]).toString(16).padStart(2, '0');
            const b = parseInt(match[3]).toString(16).padStart(2, '0');
            
            return `#${r}${g}${b}`.toUpperCase();
        };
        
        return {
            background: rgbToHex(backgroundColor),
            text: rgbToHex(color),
            backgroundRaw: backgroundColor,
            textRaw: color
        };
    }
    
        // Function to apply contrast correction
    function applyContrastFix(element, fix, originalColors) {
        console.log(`🎨 Applying correction: ${fix.description}`);
        
        // Create unique CSS class for this correction
        const fixId = `ada-contrast-fix-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
        element.classList.add(fixId);
        
        // Create CSS rule
        const style = document.createElement('style');
        style.textContent = `
            .${fixId} {
                background-color: ${fix.newBg} !important;
                color: ${fix.newText} !important;
            }
            
            /* Ensure child elements inherit the correct color */
            .${fixId} * {
                color: inherit !important;
            }
            
            /* Specific corrections for common elements */
            .${fixId}.error_message,
            .${fixId} .error_message {
                color: ${fix.newText} !important;
                background-color: ${fix.newBg} !important;
            }
            
            .${fixId} .form-message {
                color: ${fix.newText} !important;
            }
            
            /* Keep decorative icons and elements */
            .${fixId} .error-icon,
            .${fixId} svg,
            .${fixId} img {
                opacity: 0.8;
            }
        `;
        
        document.head.appendChild(style);
        
        // Mark element as fixed
        element.setAttribute('data-ada-contrast-fixed', 'true');
        element.setAttribute('data-ada-contrast-ratio', fix.ratio.toFixed(1));
        element.setAttribute('data-ada-contrast-method', fix.type);
        
        return {
            element,
            fixId,
            style,
            originalColors,
            newColors: {
                background: fix.newBg,
                text: fix.newText
            },
            ratio: fix.ratio
        };
    }
    
    // Function to find and fix problematic elements
    function findAndFixContrastIssues() {
        console.log('🔍 Finding elements with contrast problems...');
        
        const fixes = [];
        let elementsProcessed = 0;
        let elementsFixed = 0;
        
        // Find elements by specific selectors
        Object.entries(CONTRAST_FIXES.selectors).forEach(([category, selectors]) => {
            console.log(`📋 Processing category: ${category}`);
            
            selectors.forEach(selector => {
                try {
                    const elements = document.querySelectorAll(selector);
                    console.log(`   Selector "${selector}": ${elements.length} elementos encontrados`);
                    
                    elements.forEach(element => {
                        if (element.hasAttribute('data-ada-contrast-fixed')) {
                            return; // Already processed
                        }
                        
                        elementsProcessed++;
                        const colors = getElementColors(element);
                        
                        if (!colors.background || !colors.text) {
                            return; // Could not get colors
                        }
                        
                        // Check if needs correction
                        const needsFix = checkIfNeedsFix(colors, element);
                        
                        if (needsFix) {
                            const fix = getBestFix(colors, element);
                            if (fix) {
                                const appliedFix = applyContrastFix(element, fix, colors);
                                fixes.push(appliedFix);
                                elementsFixed++;
                                
                                console.log(`✅ Element fixed: ${selector}`);
                                console.log(`   Original ratio: ${needsFix.ratio.toFixed(1)}:1`);
                                console.log(`   New ratio: ${fix.ratio.toFixed(1)}:1`);
                            }
                        }
                    });
                } catch (error) {
                    console.warn(`⚠️  Error procesando selector "${selector}":`, error);
                }
            });
        });
        
        // Find elements by specific colors in inline styles
        findElementsByInlineStyles();
        
        console.log(`📊 Summary: ${elementsProcessed} elements processed, ${elementsFixed} elements fixed`);
        return fixes;
    }
    
    // Function to check if an element needs correction
    function checkIfNeedsFix(colors, element) {
        if (!colors.background || !colors.text) return false;
        
        const ratio = getContrastRatio(colors.background, colors.text);
        
        // WCAG 2.2 requires minimum 4.5:1 for normal text
        if (ratio < 4.5) {
            return {
                ratio,
                background: colors.background,
                text: colors.text,
                element
            };
        }
        
        return false;
    }
    
    // Function to get the best correction
    function getBestFix(colors, element) {
        // Find specific correction for this background color
        const bgColor = colors.background.toUpperCase();
        const textColor = colors.text.toUpperCase();
        
        // Check predefined corrections
        for (const [colorKey, colorConfig] of Object.entries(CONTRAST_FIXES.colorMappings)) {
            if (bgColor === colorKey.toUpperCase() || textColor === colorConfig.originalText) {
                // Select the best correction (prefer to keep brand colors)
                const bestFix = colorConfig.fixes.find(fix => fix.ratio >= 4.5) || colorConfig.fixes[0];
                return bestFix;
            }
        }
        
        // Generate automatic correction
        return generateAutomaticFix(colors);
    }
    
        // Function to generate automatic correction
    function generateAutomaticFix(colors) {
        const currentRatio = getContrastRatio(colors.background, colors.text);
        
        // If the ratio is close to 4.5, make minimum adjustment
        if (currentRatio >= 4.0) {
            // Slightly darken the text
            const darkerText = darkenColor(colors.text, 0.2);
            const newRatio = getContrastRatio(colors.background, darkerText);
            
            if (newRatio >= 4.5) {
                return {
                    type: 'auto_darken_text',
                    newBg: colors.background,
                    newText: darkerText,
                    ratio: newRatio,
                    description: 'Text color changed to a darker color'
                };
            }
        }
        
        // Fallback: use black for text
        return {
            type: 'auto_black_text',
            newBg: colors.background,
            newText: '#000000',
            ratio: getContrastRatio(colors.background, '#000000'),
            description: 'Text color changed to black'
        };
    }
    
    // Function to darken a color
    function darkenColor(hex, factor) {
        const r = Math.max(0, parseInt(hex.slice(1, 3), 16) * (1 - factor));
        const g = Math.max(0, parseInt(hex.slice(3, 5), 16) * (1 - factor));
        const b = Math.max(0, parseInt(hex.slice(5, 7), 16) * (1 - factor));
        
        const toHex = (n) => Math.round(n).toString(16).padStart(2, '0');
        return `#${toHex(r)}${toHex(g)}${toHex(b)}`.toUpperCase();
    }
    
    // Function to find elements with problematic inline styles
    function findElementsByInlineStyles() {
        console.log('🔍 Buscando elementos con estilos inline problemáticos...');
        
        const allElements = document.querySelectorAll('*[style]');
        let inlineFixed = 0;
        
        allElements.forEach(element => {
            const style = element.getAttribute('style');
            
            // Find specific problematic colors (Issue 18: #7BA6DE)
            const hasProblematicColors = 
                style.includes('#7BA6DE') || style.includes('#7ba6de') ||
                style.includes('rgb(123, 166, 222)') || style.includes('rgb(123,166,222)') ||
                style.includes('#C7D9F1') || style.includes('#c7d9f1') ||
                style.includes('#C42323') || style.includes('#c42323');
            
            if (hasProblematicColors && !element.hasAttribute('data-ada-contrast-fixed')) {
                const colors = getElementColors(element);
                const needsFix = checkIfNeedsFix(colors, element);
                
                if (needsFix) {
                    const fix = getBestFix(colors, element);
                    if (fix) {
                        applyContrastFix(element, fix, colors);
                        inlineFixed++;
                        console.log(`✅ Inline element fixed: ${element.tagName.toLowerCase()}`);
                        console.log(`   Original: bg=${colors.background}, text=${colors.text}, ratio=${needsFix.ratio.toFixed(1)}:1`);
                        console.log(`   Fixed: bg=${fix.newBg}, text=${fix.newText}, ratio=${fix.ratio.toFixed(1)}:1`);
                    }
                }
            }
        });
        
        console.log(`📊 Inline elements fixed: ${inlineFixed}`);
    }
    
    // Function to setup dynamic content observer
    function setupDynamicContentObserver() {
        const observer = new MutationObserver(function(mutations) {
            let shouldReprocess = false;
            
            mutations.forEach(function(mutation) {
                if (mutation.type === 'childList') {
                    mutation.addedNodes.forEach(function(node) {
                        if (node.nodeType === 1) { // Element node
                                // Check if the new node has problematic colors
                            const hasProblematicStyles = node.style && (
                                node.style.backgroundColor || 
                                node.style.color ||
                                node.getAttribute('style')
                            );
                            
                            const containsProblematicElements = node.querySelector && (
                                node.querySelector('.error_message') ||
                                node.querySelector('.form-message') ||
                                node.querySelector('[style*="#7BA6DE"]') ||
                                node.querySelector('[style*="#7ba6de"]') ||
                                node.querySelector('[style*="7BA6DE"]') ||
                                node.querySelector('.okendo-review') ||
                                node.querySelector('.stock-status') ||
                                node.querySelector('.in-stock') ||
                                node.querySelector('[style*="#C7D9F1"]') ||
                                node.querySelector('[style*="#C42323"]')
                            );
                            
                            if (hasProblematicStyles || containsProblematicElements) {
                                shouldReprocess = true;
                            }
                        }
                    });
                }
                
                // Also check for style attribute changes
                if (mutation.type === 'attributes' && mutation.attributeName === 'style') {
                    shouldReprocess = true;
                }
            });
            
            if (shouldReprocess) {
                setTimeout(() => {
                    console.log('🔄 Reprocessing contrast due to DOM changes...');
                    findAndFixContrastIssues();
                }, 200);
            }
        });
        
        observer.observe(document.body, {
            childList: true,
            subtree: true,
            attributes: true,
            attributeFilter: ['style', 'class']
        });
        
        return observer;
    }
    
    // Utility function for testing
    function getContrastReport() {
        const report = {
            elementsChecked: 0,
            elementsFixed: 0,
            contrastIssues: [],
            fixedElements: []
        };
        
        // Analyze all elements with corrections
        const fixedElements = document.querySelectorAll('[data-ada-contrast-fixed="true"]');
        report.elementsFixed = fixedElements.length;
        
        fixedElements.forEach(element => {
            const ratio = element.getAttribute('data-ada-contrast-ratio');
            const method = element.getAttribute('data-ada-contrast-method');
            
            report.fixedElements.push({
                element,
                ratio: parseFloat(ratio),
                method,
                tagName: element.tagName.toLowerCase(),
                classes: element.className
            });
        });
        
        // Find elements that might still have problems
        const allElements = document.querySelectorAll('*');
        allElements.forEach(element => {
            if (!element.hasAttribute('data-ada-contrast-fixed')) {
                const colors = getElementColors(element);
                if (colors.background && colors.text) {
                    report.elementsChecked++;
                    const ratio = getContrastRatio(colors.background, colors.text);
                    
                    if (ratio < 4.5) {
                        report.contrastIssues.push({
                            element,
                            ratio,
                            background: colors.background,
                            text: colors.text
                        });
                    }
                }
            }
        });
        
        return report;
    }
    
    // Main initialization function
    function initialize() {
        console.log('🚀 Inicializando ADA Contrast Fix...');
        
        // Apply immediate corrections
        const fixes = findAndFixContrastIssues();
        
        // Setup observer for dynamic content
        const observer = setupDynamicContentObserver();
        
        console.log('✅ ADA Contrast Fix inicializado correctamente');
        console.log(`   🎨 Elements fixed: ${fixes.length}`);
        
        return { fixes, observer };
    }
    
    // Execute initialization
    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', initialize);
    } else {
        initialize();
    }
    
    // Also execute after a delay for dynamic content
    setTimeout(initialize, 1000);
    
    // Expose functions globally for testing and debugging
    window.adaContrastFix = {
        findAndFixContrastIssues,
        getContrastRatio,
        getContrastReport,
        applyContrastFix,
        config: CONTRAST_FIXES
    };
    
    console.log('🎨 ADA Contrast Fix loaded. Functions available in window.adaContrastFix');
    
})();
